import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, X, Cylinder } from "lucide-react";
import StatusBadge, { STATUS_LIST, STATUS_LABELS } from "@/components/StatusBadge";

export default function CylinderList() {
  const [cylinders, setCylinders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [pos, setPos] = useState([]);
  const [filters, setFilters] = useState({
    customer_id: "",
    plant_id: "",
    purchase_order_id: "",
    cylinder_status: "",
    search: "",
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/plants").then((r) => setPlants(r.data));
    api.get("/purchase-orders").then((r) => setPos(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setLoading(true);
    api.get("/cylinders", { params }).then((r) => setCylinders(r.data)).finally(() => setLoading(false));
  }, [filters]);

  const filteredPlants = filters.customer_id
    ? plants.filter((p) => p.customer_id === filters.customer_id)
    : plants;
  const filteredPos = filters.customer_id
    ? pos.filter((p) => p.customer_id === filters.customer_id)
    : pos;

  function clearFilters() {
    setFilters({ customer_id: "", plant_id: "", purchase_order_id: "", cylinder_status: "", search: "" });
  }

  const hasActiveFilters = Object.values(filters).some((v) => v);

  return (
    <div data-testid="cylinder-list-page" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Master Data</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
            Cylinders
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {cylinders.length} cylinder{cylinders.length !== 1 ? "s" : ""} matching current filters
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-600">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                data-testid="filter-search"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Cylinder number or serial..."
                className="pl-8 h-9"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-600">Customer</Label>
            <Select
              value={filters.customer_id || "all"}
              onValueChange={(v) => setFilters({ ...filters, customer_id: v === "all" ? "" : v, plant_id: "", purchase_order_id: "" })}
            >
              <SelectTrigger data-testid="filter-customer" className="h-9 mt-1">
                <SelectValue placeholder="All customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-600">Plant</Label>
            <Select
              value={filters.plant_id || "all"}
              onValueChange={(v) => setFilters({ ...filters, plant_id: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="filter-plant" className="h-9 mt-1">
                <SelectValue placeholder="All plants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plants</SelectItem>
                {filteredPlants.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-600">PO</Label>
            <Select
              value={filters.purchase_order_id || "all"}
              onValueChange={(v) => setFilters({ ...filters, purchase_order_id: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="filter-po" className="h-9 mt-1">
                <SelectValue placeholder="All POs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All POs</SelectItem>
                {filteredPos.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.po_number}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-600">Status</Label>
            <Select
              value={filters.cylinder_status || "all"}
              onValueChange={(v) => setFilters({ ...filters, cylinder_status: v === "all" ? "" : v })}
            >
              <SelectTrigger data-testid="filter-status" className="h-9 mt-1">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_LIST.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="mt-3 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              data-testid="clear-filters"
              onClick={clearFilters}
              className="h-8 text-xs"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear filters
            </Button>
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
          </div>
        ) : cylinders.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Cylinder className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <div className="text-sm">No cylinders match the current filters.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Cyl #</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">PO #</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">OA #</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Specification</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Current Stage</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cylinders.map((c) => (
                  <tr key={c.id} data-testid={`row-${c.cylinder_number}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/cylinders/${c.id}`}
                        className="font-mono text-[13px] font-bold text-blue-900 hover:text-blue-700 hover:underline"
                        data-testid={`link-${c.cylinder_number}`}
                      >
                        #{c.cylinder_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-800">{c.customer_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{c.po_number || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{c.oa_number || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">
                      {c.spec_summary || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      <span className="font-semibold">{c.current_stage || c.cylinder_status}</span>
                      {c.responsible_department && (
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{c.responsible_department}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">
                      {c.created_at ? c.created_at.slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={c.cylinder_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
